import { useEffect, useRef, useState } from 'react'
import './HackerCursor.css'

export default function HackerCursor() {
    const cursorRef = useRef<HTMLDivElement>(null)
    const positionRef = useRef({ x: -100, y: -100 }) // Start off-screen
    const targetRef = useRef({ x: -100, y: -100 })

    // Always render custom cursor logic. Visibility is controlled by CSS media queries.
    // This allows hybrid devices (laptops with touchscreens) to still show the cursor.
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            targetRef.current = { x: e.clientX, y: e.clientY }
            if (!visible) setVisible(true)
        }

        const handleEnter = () => setVisible(true)
        const handleLeave = () => setVisible(false)
        const handleDown = () => {
            if (cursorRef.current) cursorRef.current.classList.add('clicking')
        }
        const handleUp = () => {
            if (cursorRef.current) cursorRef.current.classList.remove('clicking')
        }

        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseenter', handleEnter)
        window.addEventListener('mouseleave', handleLeave)
        window.addEventListener('mousedown', handleDown)
        window.addEventListener('mouseup', handleUp)

        // High-performance animation loop
        let rafId: number
        const animate = () => {
            if (cursorRef.current) {
                // Lerp for smooth follow (0.4 = fast/snappy/bolt-like)
                const dt = 0.4

                const dx = targetRef.current.x - positionRef.current.x
                const dy = targetRef.current.y - positionRef.current.y

                // If distance is very small, snap to target to stop micro-calculations
                if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                    positionRef.current = { ...targetRef.current }
                } else {
                    positionRef.current.x += dx * dt
                    positionRef.current.y += dy * dt
                }

                // Align tip of bolt (top-center) to mouse position
                cursorRef.current.style.transform = `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0) translate(-50%, -10%)`
            }
            rafId = requestAnimationFrame(animate)
        }
        rafId = requestAnimationFrame(animate)

        return () => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseenter', handleEnter)
            window.removeEventListener('mouseleave', handleLeave)
            window.removeEventListener('mousedown', handleDown)
            window.removeEventListener('mouseup', handleUp)
            cancelAnimationFrame(rafId)
        }
    }, [visible])

    return (
        <div
            ref={cursorRef}
            className={`hacker-cursor ${visible ? 'visible' : 'hidden'}`}
        >
            <div className="cursor-bolt">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="none" />
                </svg>
            </div>
        </div>
    )
}

